/* eslint-disable max-len */
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import _ from 'lodash';
import { RecurringRouteService } from '../recurring-route-details/recurring-route.service';
import moment from 'moment';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';

@Component({
  selector: 'route-picker',
  templateUrl: './route-picker.component.html',
  styleUrls: ['./route-picker.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class RoutePickerComponent implements OnInit {

  @Input() recurringRouteMasterUid?: string; //To show 'Current series' label on master route list
  @Input() routeUid?: string;   //Pass inputs to block user from selected the route
  @Input() extraClass?: string;
  @Input() timezone?: string = this.utilityService.timeZone;
  @Output() actionEmitter: EventEmitter<any> = new EventEmitter<any>();

  //Navigation
  selectedView: ViewType = 'INITIAL';

  //General
  routeType: RouteType;
  dateTimeFormat: string = this.utilityService.dateTimeFormat;
  /* eslint-disable @typescript-eslint/naming-convention */
  routePagination = {
    'ONE_OFF_ROUTES': { page: 1, currentPage: 1, totalPages: 1, isLoading: false, keyword: '' }, 'RECURRING_ROUTE_MASTER': { page: 1, currentPage: 1, totalPages: 1, isLoading: false, keyword: '' },
    'RECURRING_ROUTE_INSTANCES': { page: 1, currentPage: 1, totalPages: 1, isLoading: false, keyword: '', masterRouteUid: '' }
  };
  routeList = { 'ONE_OFF_ROUTES': [], 'RECURRING_ROUTE_MASTER': [], 'RECURRING_ROUTE_INSTANCES': [] };
  routeParam = { 'ONE_OFF_ROUTES': {}, 'RECURRING_ROUTE_MASTER': {}, 'RECURRING_ROUTE_INSTANCES': {} };
  selectedRouteMasterDetails: any;
  /* eslint-enable @typescript-eslint/naming-convention */


  constructor(
    private localisationService: LocalisationService,
    private dataCollectorService: DataCollectorService,
    private recurringRouteService: RecurringRouteService,
    private utilityService: UtilityService
  ) { }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
  }

  //Navigation
  switchView(selectedView: ViewType, data?: any): void {
    const previousView = this.selectedView;
    if (selectedView !== this.selectedView) {
      this.selectedView = selectedView;
      this.selectedRouteMasterDetails = this.selectedView === 'RECURRING_ROUTE_INSTANCES'? this.selectedRouteMasterDetails: null;
      this.updateRouteType();
      if (this.routeType === 'ONE_OFF_ROUTES' && (!this.routeList[this.routeType]?.length || previousView === 'INITIAL')) {
        this.routeList[this.routeType] = [];
        this.routePagination[this.routeType].page = 1;
        this.routePagination[this.routeType].keyword = '';
        this.getOneOffRoutes();
      }
      else if (this.routeType === 'RECURRING_ROUTE_MASTER' && (!this.routeList[this.routeType]?.length || previousView === 'INITIAL')) {
        this.routeList[this.routeType] = [];
        this.routePagination[this.routeType].page = 1;
        this.routePagination[this.routeType].keyword = '';
        this.getRecurringRoutes();
      }
      else if (this.routeType === 'RECURRING_ROUTE_INSTANCES' && data?.recurringRouteMaster) {
        this.routeList[this.routeType] = [];
        this.routePagination[this.routeType].page = 1;
        this.routePagination[this.routeType].keyword = '';
        this.routePagination[this.routeType]['masterRouteUid'] = data?.recurringRouteMaster?.recurring_route_uid;
        this.selectedRouteMasterDetails = data?.recurringRouteMaster;
        this.getRecurringRouteInstances();
      }
    }
  }

  //General
  updateRouteType(): void {
    if (this.selectedView === 'INITIAL') {
      this.routeType = null;
    }
    else {
      this.routeType = this.selectedView;
    }
  }

  updateRouteList(): void {
    if (this.routeType === 'ONE_OFF_ROUTES') {
      this.getOneOffRoutes();
    }
    else if (this.routeType === 'RECURRING_ROUTE_MASTER') {
      this.getRecurringRoutes();
    }
    else if (this.routeType === 'RECURRING_ROUTE_INSTANCES') {
      this.getRecurringRouteInstances();
    }
  }

  scrollEventHanlder(event: any): void {
    if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight) {
      this.loadMoreRoutes();
    }
  }

  loadMoreRoutes(): void {
    if (this.routePagination[this.routeType].currentPage < this.routePagination[this.routeType].totalPages) {
      this.routePagination[this.routeType].page += 1;
      this.updateRouteList();
    }
  }

  actionEventEmitter(event: any): void {
    if (this.routeType === 'RECURRING_ROUTE_INSTANCES') {
      event.data['recurring_route'] = this.selectedRouteMasterDetails;
    }
    this.actionEmitter.next(event);
    //Reset to initial picker page after a delay
    if(event?.isSelected) {
      setTimeout(()=> {
        this.switchView('INITIAL');
      }, 200);
    }
  }

  // One-off routes
  getOneOffRoutes(): void {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    this.routeParam[this.routeType] = { 'page': this.routePagination[this.routeType].page, 'count': 10, 'filter.keyword': this.routePagination[this.routeType].keyword, 'filter.is_recurrence': false };
    const getRoutesUrl = `${this.dataCollectorService.baseurl()}/routes`;
    this.routePagination[this.routeType].isLoading = true;
    this.dataCollectorService.getData(getRoutesUrl, this.routeParam.ONE_OFF_ROUTES).subscribe(
      (response: any) => {
        const routes = this.formatRouteDetails(response?.data || [], 'oneOffRoute');
        this.routeList[this.routeType] = _.uniqBy([...this.routeList[this.routeType], ...routes || []], 'route_uid');
        this.routePagination[this.routeType].currentPage = response?.current_page || 1;
        this.routePagination[this.routeType].totalPages = response?.total_pages || 1;
        this.routePagination[this.routeType].isLoading = false;
      },
      (error: any) => {
        this.routePagination[this.routeType].isLoading = false;
      }
    );
  }

  formatRouteDetails(oneOffRoutes: any[], type: 'oneOffRoute' | 'recurringRouteInstance'): any[] {
    oneOffRoutes = _.compact(oneOffRoutes);
    if (oneOffRoutes.length) {
      oneOffRoutes = oneOffRoutes.map((oneOffRoute: any) => ({
        ...oneOffRoute,
        type: type,
        startDateTime: this.recurringRouteService.formatDateTime(oneOffRoute?.departure, this.dateTimeFormat, this.timezone),
        utilisedPercentage: oneOffRoute?.jobs?.length ? this.recurringRouteService.getRouteUtilisedPercentage(oneOffRoute?.duration, oneOffRoute?.total_time): 0,
        routeHoursRemaining: this.recurringRouteService.getRouteHoursRemaining(oneOffRoute?.duration, oneOffRoute?.jobs?.length ? oneOffRoute?.total_time: 0)
      }));
      return oneOffRoutes;
    }
    return oneOffRoutes;
  }

  routeSearch(event: any, action: string): void {
    if (event?.keyCode === 13 || action === 'clear') {
      const searchKeyword = this.routePagination[this.routeType].keyword.trim();
      if (this.routeParam[this.routeType]['filter.keyword'] !== searchKeyword) {
        this.routeParam[this.routeType]['filter.keyword'] = searchKeyword;
        this.routePagination[this.routeType].page = 1;
        this.routeList[this.routeType] = [];
        this.updateRouteList();
      }
    }
  }

  //Recurring routes master
  getRecurringRoutes(): void {
    this.routeParam[this.routeType] = { 'page': this.routePagination[this.routeType].page, 'count': 10, 'filter.keyword': this.routePagination[this.routeType].keyword };
    const getRoutesUrl = `${this.dataCollectorService.baseurl()}/routes/recurring`;
    this.routePagination[this.routeType].isLoading = true;
    this.dataCollectorService.getData(getRoutesUrl, this.routeParam[this.routeType]).subscribe(
      (response: any) => {
        const recurringRoutes = this.formatRouteMasterDetails(response?.data) || [];
        this.routeList[this.routeType] = _.uniqBy([...this.routeList[this.routeType], ...recurringRoutes || []], 'recurring_route_uid');
        this.routePagination[this.routeType].currentPage = response?.current_page || 1;
        this.routePagination[this.routeType].totalPages = response?.total_pages || 1;
        this.routePagination[this.routeType].isLoading = false;
      },
      (error: any) => {
        this.routePagination[this.routeType].isLoading = false;
      }
    );
  }

  formatRouteMasterDetails(masterRouteDetails: any): any {
    masterRouteDetails = masterRouteDetails.map((rrDetails: any) => ({
      ...rrDetails,
      rrText: this.recurringRouteService.getRruleText(rrDetails?.rrule)
    }));
    return masterRouteDetails;
  }

  //Recurring routes instances
  getRecurringRouteInstances(): void {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    this.routeParam[this.routeType] = { 'page': this.routePagination[this.routeType].page, 'count': 10, 'filter.recurring_route_uid': this.routePagination[this.routeType]['masterRouteUid'] || '', 'sort': 'ASC', 'sort_by': 'departure'};
    const getRoutesUrl = `${this.dataCollectorService.baseurl()}/routes`;
    this.routePagination[this.routeType].isLoading = true;
    this.dataCollectorService.getData(getRoutesUrl, this.routeParam[this.routeType]).subscribe(
      (response: any) => {
        const routes = this.formatRouteDetails(response?.data || [], 'recurringRouteInstance');
        this.routeList[this.routeType] = _.uniqBy([...this.routeList[this.routeType], ...routes || []], 'route_uid');
        this.routePagination[this.routeType].currentPage = response?.current_page || 1;
        this.routePagination[this.routeType].totalPages = response?.total_pages || 1;
        this.routePagination[this.routeType].isLoading = false;
      },
      (error: any) => {
        this.routePagination[this.routeType].isLoading = false;
      }
    );
  }

}
export type ViewType = 'INITIAL' | 'ONE_OFF_ROUTES' | 'RECURRING_ROUTE_MASTER' | 'RECURRING_ROUTE_INSTANCES';
export type RouteType = 'ONE_OFF_ROUTES' | 'RECURRING_ROUTE_MASTER' | 'RECURRING_ROUTE_INSTANCES';
